import { useCallback, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  MobileNav,
  MobileNavHeader,
  MobileNavMenu,
  MobileNavToggle,
  Navbar,
  NavBody,
} from "@/components/ui/resizable-navbar";
import { useAuth } from "@/contexts/AuthContext";
import { useClickOutside } from "@/lib/hooks/useClickOutside";
import { ROUTES, type NavModel } from "@/lib/navigation";
import { cn } from "@/lib/utils";
import { AuthButtons } from "./AuthButtons";
import { NavMenu } from "./NavMenu";
import { PrimaryNav } from "./PrimaryNav";
import { UserMenu } from "./UserMenu";

interface AppHeaderProps {
  nav: NavModel;
}

/**
 * A fejléc az Aceternity „resizable navbar” primitívre épül (`ui/resizable-navbar.tsx`):
 * md-től logó | linksáv | avatar menü / belépés egy sorban, alatta logó + hamburger és lenyíló
 * panel. Hogy mi kerül a sorba és a panelbe, a `nav` modell dönti el – lásd `getSiteNav` /
 * `getAdminNav`.
 */
export function AppHeader({ nav }: AppHeaderProps) {
  const { user, signOut } = useAuth();
  const headerRef = useRef<HTMLElement>(null);
  const menuId = useId();
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  useClickOutside(headerRef, menuOpen, closeMenu);

  // A signOut a főoldalt újratöltve nyitja meg (a helyi cache törlése miatt)
  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <Navbar ref={headerRef}>
      <NavBody>
        <Logo className="h-10" />
        <PrimaryNav items={nav.primary} style={nav.primaryStyle} />
        <div className="flex shrink-0 items-center">
          {user ? (
            <UserMenu user={user} links={nav.userMenu} onSignOut={handleSignOut} />
          ) : (
            <AuthButtons pill />
          )}
        </div>
      </NavBody>

      <MobileNav>
        <MobileNavHeader>
          <Logo className="h-9" />
          <MobileNavToggle
            isOpen={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            aria-controls={menuId}
          />
        </MobileNavHeader>
        <MobileNavMenu id={menuId} isOpen={menuOpen} onClose={closeMenu}>
          <NavMenu
            label="Mobil navigáció"
            groups={nav.mobileGroups}
            user={user}
            onSignOut={handleSignOut}
            onNavigate={closeMenu}
          />
        </MobileNavMenu>
      </MobileNav>
    </Navbar>
  );
}

function Logo({ className }: { className?: string }) {
  return (
    <Link
      to={ROUTES.home}
      className="group relative z-20 flex shrink-0 items-center rounded-lg focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-hidden"
    >
      <img
        src="/arizona-bowling-logo.png"
        alt="Arizona Bowling főoldal"
        className={cn("transition-transform duration-300 group-hover:scale-105 dark:hidden", className)}
      />
      <img
        src="/arizona-bowling-logo-sotet.png"
        alt="Arizona Bowling főoldal"
        className={cn("hidden transition-transform duration-300 group-hover:scale-105 dark:block", className)}
      />
    </Link>
  );
}
